import { useQueries, useQuery } from '@tanstack/react-query';

import { queryClient } from '../components/queryClient';
import { useFleet } from '../hooks/useFleet';
import { useHasPermission } from '../hooks/useHasPermission';
import { Fleet, Route, RouteFileVersion } from '../interfaces';
import { RouteService } from '../services';
import { FleetUUIDs } from '../enums';

export const useRoutes = () => {
  const fleet = useFleet();
  const queryKey = ['routes', fleet.id];
  const hasPermission = useHasPermission('routes:read:list');

  const queryFn = () => RouteService.getAll();

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!fleet.id && hasPermission,
  });
};

export const useRoute = (id: number, silent = false) => {
  const fleet = useFleet();
  const queryKey = ['routes', 'route', id, fleet.id];
  const hasPermission = useHasPermission('routes:read:resource');

  const queryFn = () => RouteService.get(id, silent);

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!fleet.id && hasPermission && !!id,
  });
};

export const useRouteHistory = (id: number, enabled = true) => {
  const fleet = useFleet();
  const queryKey = ['routes', 'route', id, 'history', fleet.id];
  const hasPermission = useHasPermission('routes:read:resource');

  const queryFn = () => RouteService.getHistory(id);

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!fleet.id && hasPermission && !!id && enabled,
    staleTime: 0, // Always refetch when the component mounts
  });
};

export const useRoutefileHistory = (id: number, enabled = true) => {
  const fleet = useFleet();
  const queryKey = ['routes', 'route', id, 'fileVersionsWithDeployment', fleet.id];
  const hasPermission = useHasPermission('routes:read:resource');

  const queryFn = () => RouteService.getRoutefileHistory(id);

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!fleet.id && hasPermission && !!id && enabled,
    staleTime: 0, // Always refetch when the component mounts
  });
};

export const refreshRoutes = (fleetId: Fleet['id']) => {
  // Make sure we refetch the event in the background, WHEN NEEDED - but display
  // the cached data in the meantime, instantenously
  queryClient.invalidateQueries(['routes', fleetId]);
};

export const useRouteDefinition = (route_id: string, enabled: boolean) => {
  const fleet = useFleet();
  const queryKey = ['routes', 'definitions', route_id, fleet.id];

  const queryFn = async () => RouteService.loadRawRouteDefinition(route_id);

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!fleet.id && enabled,
  });
};

export const useRouteDefinitionAtVersion = (routeId: number, version: string, enabled: boolean) => {
  const fleet = useFleet();
  const queryKey = ['routes', 'definitions', routeId.toString(), version, fleet.id];

  const queryFn = () => RouteService.loadRawRouteDefinition(routeId.toString(), version);

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!fleet.id && enabled && !!routeId && !!version,
  });
};

export const setRouteDefinition = (route_id: string, data: any) => {
  queryClient.setQueryData(['routes', 'definitions', route_id], data);
};

export const useRouteDefinitions = (routes: Route[], enabled: boolean, versionByRouteId?: Record<number, string>) => {
  const fleet = useFleet();
  const hasPermission = useHasPermission('routes:read:resource');

  return useQueries({
    queries: routes.map((route) => {
      // When the history panel has a version selected for this route, load that version instead of
      // the latest. The versioned query key matches useRouteDefinitionAtVersion, so the base fetch
      // is shared with the diff overlay's fetch rather than duplicated.
      const version = versionByRouteId?.[route.id];

      return {
        queryKey: version
          ? ['routes', 'definitions', route.id.toString(), version, fleet.id]
          : ['routes', 'definitions', route.id.toString(), fleet.id],
        queryFn: () => RouteService.loadRawRouteDefinition(route.id.toString(), version),
        enabled: !!fleet.id && hasPermission && enabled,
      };
    }),
  });
};

export const useRouteFileVersions = () => {
  const fleet = useFleet();
  const queryKey = ['routes', 'fileVersions', fleet.id];
  const hasPermission = useHasPermission('routes:read:list');

  const queryFn = (): Promise<RouteFileVersion[]> => RouteService.getFileVersions();

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!fleet.id && hasPermission,
  });
};

export const useZonePDFs = () => {
  const fleet = useFleet();
  const queryKey = ['routes', 'zonePDFs', fleet.id];
  const hasPermission = useHasPermission('routes:read:list');

  const isWMATA = fleet.uuid === FleetUUIDs.WMATA;
  const featureEnabled = isWMATA && hasPermission;

  const queryFn = () => RouteService.getZonePDFs();

  const query = useQuery({
    queryKey,
    queryFn,
    enabled: !!fleet.id && featureEnabled,
  });

  return [query, featureEnabled] as const;
};
